import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import React, { useState } from "react";
import { Eye, EyeOff, AlertTriangle } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { passwordSchema } from "@/lib/schemas/userSchemas";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

interface ResetPasswordModalProps {
  open: boolean;
  user: { id: string; username: string; email: string | null } | null;
  onClose: () => void;
  onReset: (newPassword: string) => Promise<void>;
}

const resetPasswordSchema = z
  .object({
    newPassword: passwordSchema,
    confirmPassword: z.string().min(1, "Please confirm the password"),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>;

const REQUIREMENTS = [
  { label: "At least 8 characters", test: (v: string) => v.length >= 8 },
  { label: "At least one uppercase letter", test: (v: string) => /[A-Z]/.test(v) },
  { label: "At least one number", test: (v: string) => /[0-9]/.test(v) },
  { label: "At least one symbol", test: (v: string) => /[^a-zA-Z0-9]/.test(v) },
];

export const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({
  open,
  user,
  onClose,
  onReset,
}) => {
  const [step, setStep] = useState<"form" | "confirm">("form");
  const [showNewPw, setShowNewPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<ResetPasswordFormValues>({
    defaultValues: { newPassword: "", confirmPassword: "" },
    resolver: zodResolver(resetPasswordSchema),
  });

  const newPasswordValue = form.watch("newPassword");

  const handleClose = () => {
    if (!isSubmitting) {
      form.reset();
      setStep("form");
      setSubmitError(null);
      onClose();
    }
  };

  const onFormSubmit = async (data: ResetPasswordFormValues) => {
    setStep("confirm");
  };

  const handleConfirm = async () => {
    const newPassword = form.getValues("newPassword");
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await onReset(newPassword);
      form.reset();
      setStep("form");
      onClose();
    } catch (error: any) {
      setSubmitError(error?.message ?? "Failed to reset password");
      setStep("form");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="min-w-[30%] flex flex-col">
        <DialogHeader>
          <DialogTitle>Reset password</DialogTitle>
        </DialogHeader>
        <Separator />

        {step === "form" && (
          <>
            {submitError && (
              <Alert variant="destructive">
                <AlertDescription>{submitError}</AlertDescription>
              </Alert>
            )}
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onFormSubmit)} className="flex flex-col gap-2 mb-1">
                <FormField
                  control={form.control}
                  name="newPassword"
                  render={({ field }) => (
                    <FormItem className="space-y-1.5">
                      <FormLabel className="text-sm">New password</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type={showNewPw ? "text" : "password"}
                            autoComplete="new-password"
                            className="pr-9"
                            {...field}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            className="absolute right-1 top-1/2 -translate-y-1/2 text-muted-foreground"
                            tabIndex={-1}
                            onClick={() => setShowNewPw((v) => !v)}
                          >
                            {showNewPw ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                          </Button>
                        </div>
                      </FormControl>
                      <FormMessage />
                      {newPasswordValue.length > 0 && (
                        <ul className="mt-1 space-y-0.5">
                          {REQUIREMENTS.map((req) => (
                            <li
                              key={req.label}
                              className={`text-xs ${req.test(newPasswordValue) ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`}
                            >
                              {req.test(newPasswordValue) ? "✓" : "·"} {req.label}
                            </li>
                          ))}
                        </ul>
                      )}
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem className="space-y-1.5">
                      <FormLabel className="text-sm">Confirm password</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type={showConfirmPw ? "text" : "password"}
                            autoComplete="new-password"
                            className="pr-9"
                            {...field}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            className="absolute right-1 top-1/2 -translate-y-1/2 text-muted-foreground"
                            tabIndex={-1}
                            onClick={() => setShowConfirmPw((v) => !v)}
                          >
                            {showConfirmPw ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                          </Button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Separator />
                <div className="flex justify-end">
                  <Button
                    variant="secondary"
                    onClick={handleClose}
                    type="button"
                    className="mr-2 w-20"
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className="w-20">
                    Next
                  </Button>
                </div>
              </form>
            </Form>
          </>
        )}

        {step === "confirm" && (
          <div className="flex flex-col gap-4">
            <div className="flex items-start gap-3 rounded-md border border-amber-300 dark:border-amber-700 bg-amber-100 dark:bg-amber-900/40 p-3">
              <AlertTriangle className="size-4 mt-0.5 text-amber-600 dark:text-amber-400 shrink-0" />
              <div className="text-sm">
                <p className="font-medium">Confirm password reset</p>
                <p className="text-muted-foreground mt-1">
                  You are resetting the password for{" "}
                  <span className="font-semibold text-foreground">{user?.username}</span>
                  {user?.email && (
                    <>
                      {" "}(<span className="text-foreground">{user.email}</span>)
                    </>
                  )}
                  .
                </p>
                <p className="text-muted-foreground mt-1">
                  The old password stops working at once. A session this user already has open keeps working until its
                  sign-in expires, and cannot be renewed. On their next sign-in they must set a new password.
                </p>
              </div>
            </div>
            <Separator />
            <div className="flex justify-end">
              <Button
                variant="secondary"
                onClick={() => setStep("form")}
                type="button"
                disabled={isSubmitting}
                className="mr-2 w-20"
              >
                Back
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={isSubmitting}
                className="w-28"
              >
                {isSubmitting ? "Resetting..." : "Reset password"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
